<template>
  <pp-dialog :active="active" :max-width="560" @update:active="onToggle">
    <div class="pp-paywall">
      <billing-step
        v-if="billingInfo"
        :info="billingInfo"
        :plan="plan"
        :source="source"
        @back="billingInfo = null"
        @done="goToCheckout"
      />
      <template v-else>
      <pro-badge label="Recrutei PRO" size="md" />
      <h3 class="pp-paywall__title">Seu perfil do jeito que você quer</h3>

      <ul class="pp-paywall__benefits">
        <li v-for="benefit in benefits" :key="benefit.title">
          <span class="pp-paywall__benefit-icon">
            <component :is="benefit.icon" size="18" />
          </span>
          <div>
            <p class="pp-paywall__benefit-title">{{ benefit.title }}</p>
            <p class="pp-paywall__benefit-text">{{ benefit.text }}</p>
            <!-- Tira a dúvida na hora: o nome que a pessoa quer está livre? -->
            <div v-if="benefit.address && page && !page.slug" class="pp-paywall__check">
              <button v-if="!checkOpen" type="button" class="pp-paywall__check-toggle" @click="openCheck">
                Conferir outra URL
              </button>
              <template v-else>
                <div class="pp-paywall__check-field">
                  <span>recrutei.me/</span>
                  <input
                    ref="checkInput"
                    v-model="checkValue"
                    maxlength="40"
                    autocomplete="off"
                    spellcheck="false"
                    aria-label="Endereço que você quer"
                    placeholder="seu-nome"
                    @input="onCheckInput"
                  />
                </div>
                <p class="pp-paywall__check-status" :class="`pp-paywall__check-status--${checkStatus}`">
                  {{ checkText }}
                </p>
              </template>
            </div>
          </div>
        </li>
      </ul>

      <div class="pp-paywall__plans">
        <label
          class="pp-paywall__plan"
          :class="{ 'pp-paywall__plan--active': plan === 'monthly' }"
        >
          <input v-model="plan" type="radio" value="monthly" />
          <span class="pp-paywall__plan-name">
            <span class="pp-paywall__radio" />
            Mensal
          </span>
          <span class="pp-paywall__plan-price">
            <strong>{{ monthlyPrice }}</strong> /mês
          </span>
          <span class="pp-paywall__plan-note">Cancela quando quiser</span>
        </label>
        <label
          class="pp-paywall__plan"
          :class="{ 'pp-paywall__plan--active': plan === 'lifetime' }"
        >
          <input v-model="plan" type="radio" value="lifetime" />
          <span class="pp-paywall__plan-name">
            <span class="pp-paywall__radio" />
            Para sempre
            <span class="pp-paywall__tag">Sem mensalidade</span>
          </span>
          <span class="pp-paywall__plan-price">
            <strong>{{ lifetimePrice }}</strong> uma vez
          </span>
          <span class="pp-paywall__plan-note">Paga uma vez e fica PRO</span>
        </label>
      </div>

      <button
        type="button"
        class="pp-paywall__cta"
        :disabled="loading"
        @click="startCheckout"
      >
        {{ loading ? "Abrindo o pagamento..." : ctaLabel }}
      </button>

      <p class="pp-paywall__note">
        Pagamento seguro pela Stripe. Cartão ou Pix (Pix só no pagamento único).
      </p>
      <button type="button" class="pp-paywall__dismiss" @click="dismiss">
        Agora não
      </button>
      </template>
    </div>
  </pp-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import {
  GlobeIcon,
  VideoIcon,
  MicIcon,
  LinkIcon,
  LayoutIcon,
  EyeOffIcon,
} from "vue-feather-icons";
import PpDialog from "./PpDialog.vue";
import ProBadge from "./ProBadge.vue";
import BillingStep from "./BillingStep.vue";
import mixin from "./mixin";
import service, { errorReason, errorStatus } from "@/services/publicProfile";
import { SLUG_RE, formatCents, slugify } from "./constants";

export default {
  name: "PaywallDialog",
  components: {
    PpDialog,
    ProBadge,
    BillingStep,
    GlobeIcon,
    VideoIcon,
    MicIcon,
    LinkIcon,
    LayoutIcon,
    EyeOffIcon,
  },
  mixins: [mixin],
  props: {
    active: { type: Boolean, default: false },
    // de onde o paywall abriu, para o analytics
    source: { type: String, default: "" },
  },
  data: () => ({
    plan: "monthly",
    loading: false,
    // Dados da nota que faltam: com valor, o dialog mostra o wizard no lugar dos planos.
    billingInfo: null,
    checkOpen: false,
    checkValue: "",
    checkStatus: "idle", // idle | checking | available | taken | reserved | invalid | error
  }),
  computed: {
    ...mapGetters("publicProfile", ["prices", "page"]),
    monthlyPrice() {
      return formatCents(this.prices.monthly_cents);
    },
    lifetimePrice() {
      return formatCents(this.prices.lifetime_cents);
    },
    checkText() {
      return {
        idle: "",
        checking: "Verificando...",
        available: "Disponível. Depois de assinar, você escolhe esse endereço.",
        taken: "Esse endereço já está em uso.",
        reserved: "Esse endereço não pode ser usado. Tente outro.",
        invalid: "Use de 3 a 40 letras minúsculas, números ou hífen.",
        error: "Não deu para verificar agora.",
      }[this.checkStatus];
    },
    ctaLabel() {
      return this.plan === "monthly"
        ? `Assinar por ${this.monthlyPrice}/mês`
        : `Pagar ${this.lifetimePrice} e liberar para sempre`;
    },
    benefits() {
      const page = this.page || {};
      const current = page.path ? `recrutei.me/${page.path}` : "";
      return [
        {
          address: true,
          icon: "globe-icon",
          title: page.suggested_slug
            ? `recrutei.me/${page.suggested_slug}`
            : "Endereço com seu nome",
          text: current
            ? `Seu link com o seu nome, no lugar de ${current}`
            : "Você escolhe o link do seu perfil",
        },
        { icon: "video-icon", title: "Vídeo de apresentação", text: "Seu vídeo dentro do perfil" },
        { icon: "mic-icon", title: "Áudio de apresentação", text: "Com player no perfil" },
        { icon: "link-icon", title: "Links", text: "LinkedIn, portfólio, GitHub e redes" },
        { icon: "layout-icon", title: "Modelos PRO", text: "Animado, Plataforma, Gamer, Terminal e mais 5" },
        { icon: "eye-off-icon", title: "Você escolhe os botões", text: "Tire o selo, o PDF ou o compartilhar" },
      ];
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) {
        if (value) {
          this.openedAt = Date.now();
          this.checkedSlugs = 0;
          this.ppTrack("public_profile_paywall_opened", {
            source: this.source,
            default_plan: this.plan,
            suggested_slug: (this.page && this.page.suggested_slug) || null,
          });
        }
      },
    },
    plan(value, from) {
      if (!this.active) return;
      this.ppTrack("public_profile_paywall_plan_selected", {
        plan: value,
        from,
        price_cents: this.priceOf(value),
        source: this.source,
      });
    },
  },
  beforeDestroy() {
    clearTimeout(this.checkTimer);
  },
  methods: {
    priceOf(plan) {
      return plan === "lifetime" ? this.prices.lifetime_cents : this.prices.monthly_cents;
    },
    openCheck() {
      this.ppTrack("public_profile_paywall_slug_check_opened", { source: this.source });
      this.checkOpen = true;
      this.$nextTick(() => {
        // Dentro do v-for dos benefícios, o ref vira lista.
        const input = [].concat(this.$refs.checkInput || [])[0];
        if (input) input.focus();
      });
    },
    onCheckInput() {
      this.checkValue = slugify(this.checkValue);
      clearTimeout(this.checkTimer);
      if (!this.checkValue) {
        this.checkStatus = "idle";
        return;
      }
      if (!SLUG_RE.test(this.checkValue)) {
        this.checkStatus = "invalid";
        return;
      }
      this.checkStatus = "checking";
      const slug = this.checkValue;
      this.checkTimer = setTimeout(async () => {
        try {
          const result = await service.slugAvailability(slug);
          if (slug !== this.checkValue) return;
          this.checkStatus = result.available ? "available" : result.reason || "taken";
          this.checkedSlugs = (this.checkedSlugs || 0) + 1;
          this.ppTrack("public_profile_slug_checked", {
            value: slug,
            status: this.checkStatus,
            origin: "paywall",
            source: this.source,
            attempt: this.checkedSlugs,
          });
        } catch (e) {
          if (slug === this.checkValue) this.checkStatus = "error";
        }
      }, 400);
    },
    onToggle(value) {
      if (!value && this.active && !this.leavingToCheckout) {
        this.ppTrack("public_profile_paywall_closed", {
          source: this.source,
          step: this.billingInfo ? "billing" : "plans",
          plan: this.plan,
          price_cents: this.priceOf(this.plan),
          seconds_open: this.openedAt ? Math.round((Date.now() - this.openedAt) / 1000) : null,
          slug_checks: this.checkedSlugs || 0,
        });
      }
      if (!value) this.billingInfo = null;
      this.$emit("update:active", value);
    },
    dismiss() {
      this.ppTrack("public_profile_paywall_dismiss_clicked", { source: this.source, plan: this.plan });
      this.onToggle(false);
    },
    async startCheckout() {
      if (this.loading) return;
      this.loading = true;
      this.ppTrack("public_profile_checkout_started", {
        plan: this.plan,
        price_cents: this.priceOf(this.plan),
        currency: this.prices.currency || "brl",
        source: this.source,
        seconds_in_paywall: this.openedAt ? Math.round((Date.now() - this.openedAt) / 1000) : null,
      });
      try {
        // Antes da Stripe: CPF e endereço para a nota, se ainda faltar algum.
        const info = await service.billingInfo();
        if (!info.complete) {
          this.loading = false;
          this.billingInfo = info;
          this.ppTrack("public_profile_billing_asked", {
            has_cpf: !!info.has_cpf,
            missing_steps: info.has_cpf ? ["address"] : ["cpf", "address"],
            plan: this.plan,
            source: this.source,
          });
          return;
        }
      } catch (error) {
        this.loading = false;
        this.ppTrack("public_profile_checkout_open_failed", {
          step: "billing_info",
          plan: this.plan,
          source: this.source,
          status: errorStatus(error),
          reason: errorReason(error),
        });
        this.ppError("Não conseguimos abrir o pagamento agora. Tente de novo em instantes.");
        return;
      }
      await this.goToCheckout();
    },
    async goToCheckout() {
      this.loading = true;
      try {
        const { checkout_url: url } = await service.checkout(this.plan, this.companyLabel);
        if (!url) throw new Error("checkout_url vazio");
        // Salva o que falta antes de sair da pagina (o rascunho volta da Stripe pela sessao).
        await this.$store.dispatch("publicProfile/flush").catch(() => {});
        this.leavingToCheckout = true;
        this.ppTrack("public_profile_checkout_redirected", {
          plan: this.plan,
          price_cents: this.priceOf(this.plan),
          source: this.source,
          seconds_in_paywall: this.openedAt ? Math.round((Date.now() - this.openedAt) / 1000) : null,
        });
        window.location.href = url;
      } catch (error) {
        this.loading = false;
        this.ppTrack("public_profile_checkout_open_failed", {
          step: "checkout",
          plan: this.plan,
          source: this.source,
          status: errorStatus(error),
          reason: errorReason(error),
        });
        if (errorStatus(error) === 409) {
          this.ppTrack("public_profile_already_pro", { source: this.source });
          this.ppNotify("Você já é PRO", "primary");
          this.onToggle(false);
          this.$store.dispatch("publicProfile/fetch", { keepDraft: true }).catch(() => {});
          return;
        }
        if (errorReason(error) === "billing_required") {
          this.billingInfo = await service.billingInfo().catch(() => null);
          if (this.billingInfo) return;
        }
        this.ppError("Não conseguimos abrir o pagamento agora. Tente de novo em instantes.");
      }
    },
  },
};
</script>

<style lang="scss">
.pp-paywall {
  &__check {
    margin-top: 6px;
  }

  &__check-toggle {
    padding: 0;
    border: none;
    background: none;
    color: #6554c0;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-decoration: underline;
    cursor: pointer;
  }

  &__check-field {
    display: flex;
    align-items: center;
    gap: 2px;
    max-width: 320px;
    height: 36px;
    padding: 0 12px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    background: #fff;
    color: #696969;
    font-size: 14px;

    input {
      flex: 1;
      min-width: 60px;
      border: none;
      outline: none;
      background: transparent;
      color: #333;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
    }

    &:focus-within {
      border-color: #6554c0;
    }
  }

  &__check-status {
    margin: 4px 0 0;
    font-size: 12px;
    color: #696969;

    &--available {
      color: #309e3a;
    }

    &--taken,
    &--reserved,
    &--invalid,
    &--error {
      color: #de2348;
    }
  }

  display: flex;
  flex-direction: column;
  font-family: Roboto, sans-serif;

  > .pp-pro-badge {
    align-self: flex-start;
  }

  &__title {
    margin: 12px 40px 20px 0;
    color: #333;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
  }

  &__benefits {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin: 0 0 24px;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
  }

  &__benefit-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    background: rgba(101, 84, 192, 0.1);
    color: #6554c0;
  }

  &__benefit-title {
    color: #333;
    font-size: 16px;
    font-weight: 500;
  }

  &__benefit-text {
    color: #696969;
    font-size: 14px;
  }

  &__plans {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  &__plan {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    &--active {
      border-color: #6554c0;
      box-shadow: 0 0 0 1px #6554c0;

      .pp-paywall__radio {
        border-color: #6554c0;

        &::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: #6554c0;
        }
      }
    }
  }

  &__radio {
    position: relative;
    display: inline-block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1px solid #a8a8a8;
    border-radius: 9999px;
  }

  &__plan-name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    color: #333;
    font-size: 16px;
  }

  &__tag {
    padding: 2px 6px;
    border: 0.5px solid #86efac;
    border-radius: 4px;
    background: #dcfce7;
    color: #166534;
    font-size: 11px;
    font-weight: 500;
  }

  &__plan-price {
    color: #696969;
    font-size: 14px;

    strong {
      color: #333;
      font-size: 22px;
      font-weight: 600;
    }
  }

  &__plan-note {
    color: #696969;
    font-size: 13px;
  }

  &__cta {
    height: 48px;
    border: none;
    border-radius: 8px;
    background: #6554c0;
    color: #fff;
    font-family: Roboto, sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:hover {
      filter: brightness(0.92);
    }

    &:disabled {
      opacity: 0.7;
      cursor: wait;
    }
  }

  &__note {
    margin-top: 12px;
    color: #696969;
    font-size: 13px;
    text-align: center;
  }

  &__dismiss {
    align-self: center;
    margin-top: 12px;
    border: none;
    background: none;
    color: #4f4f4f;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
